class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.closest('cart-items').updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}
customElements.define('quantity-input', QuantityInput);

class CartItems extends HTMLElement {
  constructor() {
    super();

    // helper variables
    this.cartTotalValue = 0;
    this.cartTotalDiscount = 0;
    this.cartOriginalPrice = 0;

    // elements
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    // events
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);
    this.addEventListener('change', this.debouncedOnChange.bind(this));

    // dev events
    document.addEventListener('ajaxProduct:added', () => {
      this.renderAndShowItems()
    })
  }

  /**
   *  Debounced onChange event handler. Updates the cart when the user stops clicking quantity buttons.
   * @param {*} event 
   */
  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  /**
   * Returns an array of sections to render when the cart is updated. Limit: 5 sections.
   * Does not make the request to the server, only returns the section names to render.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Array} Array of sections to render
   */
  getSectionsToRender() {
    return [
      {
        id: document.querySelector('.main-cart').dataset.id,
        selector: '#cart-items',
      },
      {
        id: document.querySelector('.main-cart').dataset.id,
        selector: '#cart-shipping-notification'
      },
      {
        id: document.querySelector('.main-cart').dataset.id,
        selector: '#cart-footer'
      },
      {
        id: document.querySelector('.main-cart').dataset.id,
        selector: '#cart-additionals'
      }
    ];
  }

  /**
   * Returns the HTML value of a section
   * @param {string} html 
   * @param {string} selector 
   * @returns {string} HTML value of the section
   */
  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  /**
   * This function is always called when the cart is updated.
   * @param {string} json 
   */
  updateCartSections (json) {
    const sections = json.sections || json;

    this.getSectionsToRender().forEach((section) => {
      const sectionId = `shopify-section-${section.id}`;
      const elementToReplace = document.getElementById(sectionId).querySelector(section.selector) || document.getElementById(section.id);
      if (!elementToReplace) return;

      const HTML = this.getSectionInnerHTML(sections[section.id], section.selector);

      // clear
      elementToReplace.innerHTML = '';

      // generate nodes from HTML string
      const nodes = new DOMParser().parseFromString(HTML, 'text/html').body.childNodes;

      // clone nodes and add them to array
      const clonedNodes = Array.from(nodes).map((node) => node.cloneNode(true));

      // append nodes to element
      clonedNodes.forEach((node) => elementToReplace.appendChild(node));

      if (section.selector.includes('shipping-notification')) {
        this.updateShippingNotification(section.selector)
      }
    });
  }

  /**
   * This function handles multi-currency support for the shipping notification bar.
   * This is called when item is added to cart or when the cart is updated.
   * @param {string} selector 
   * @param {string*} json - Cart JSON
   */
  updateShippingNotification(selector) {
    const shippingNotification = document.querySelector(selector);

    const calculateCurrencyConversion = (amount) => {
      return Math.round(parseInt(amount) * (parseInt(Shopify.currency.rate) || 1))
    }

    const totalPrice = parseInt(shippingNotification.dataset.price);
    const freeShippingThreshold = calculateCurrencyConversion(shippingNotification.dataset.threshold);

    const amountLeft = freeShippingThreshold - totalPrice;
    const stringToReplace = shippingNotification.dataset.stringToReplace;

    if (amountLeft > 0) {
      shippingNotification.innerHTML = shippingNotification.innerHTML.replace(stringToReplace, Woolman.Utils.formatMoney(amountLeft));
    }

    shippingNotification.removeAttribute('hidden');
  }

  /**
   * Fetches fresh sections from the server and updates the cart. This is called when item is added to cart.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Promise} Promise that resolves when the cart is updated
   */
  async renderAndShowItems() {
    try {
      const sections = this.getSectionsToRender().map((section) => section.id).join(',')
      const res = await fetch(`?sections=${sections}`)

      const json = await res.json(); // contains sections and their HTML
      if (json.errors) {
        throw json.errors;
      }

      this.updateCartSections(json);

      const parentDrawer = this.closest('[data-drawer]');
      if (!parentDrawer) return;
      Woolman.ModalsAndDrawers.showModalOrDrawer(parentDrawer.getAttribute('id'))
    } catch(errors) {
      console.error(errors)
    }
  }

  /**
   * Updates the quantity of a line item in the cart. This is called when the user changes the quantity of an item in the cart.
   * @param {*} line 
   * @param {*} quantity 
   * @param {*} name 
   */
  async updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    try {
      const res = await fetch(`${routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: `application/json`,
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });

      const json = await res.json(); // contains the cart JSON
      if (json.errors) {
        throw json.errors;
      }

      this.updateCartSections(json);

      const event = new CustomEvent('cart:update', {
        detail: json,
      });
      document.dispatchEvent(event);

      this.updateLiveRegions(line, json.item_count);

      const lineItem = document.getElementById(`CartItem-${line}`);
      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) lineItem.querySelector(`[name="${name}"]`).focus();

      this.disableLoading();
    } catch (errors) {
      console.error(errors);
      this.disableLoading();
    }
  }

  /**
   * Updates the live regions for the cart.
   * @param {*} line 
   * @param {*} itemCount 
   */
  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      document.getElementById(`Line-item-error-${line}`).querySelector('.cart-item__error-text').innerHTML = window.cartStrings.quantityError.replace('[quantity]', document.getElementById(`Quantity-${line}`).value);
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  enableLoading(line) {
    this.classList.add('is-processing');
    this.querySelectorAll(`#CartItem-${line} .loading-overlay`).forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement.blur();
  }

  disableLoading() {
    this.classList.remove('is-processing');
    this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
  }
}
customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: {
           'Content-Type': 'application/json',
            Accept: `application/json`,
          },
          ...{ body }
        });
      }, 300))
    }
  });
};

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.loadRecommendations();
      document.addEventListener('ajaxProduct:added', this.loadRecommendations.bind(this))
    }

    async loadRecommendations() {
      if (!this.dataset.productId || this.dataset.productId === '') return;

      try {
        const url = new URL(location.origin + routes.product_recommendations_url + '.json')
        url.searchParams.set('product_id', this.dataset.productId)
        url.searchParams.set('limit', this.dataset.limit || 10)
  
        const res = await fetch(url)
        const { products } = await res.json();

        if (!products || products.length == 0) {
          throw 'No product recommendations found for products in the cart'
        }

        this.productsContainer = this.querySelector('[data-products-ajax]')
        this.productsContainer.innerHTML = '';

        products.forEach(async (product) => {
          const template = this.querySelector('template').content.firstElementChild.cloneNode(true);
          const filename = product.featured_image.split('/').pop();
          const lastDotIndex = filename.lastIndexOf('.');
          const newFilename = `${filename.substring(0, lastDotIndex)}_300x${filename.substring(lastDotIndex)}`;
          const url = product.featured_image.replace(filename, newFilename);
          const price = Woolman.Utils.formatMoney(product.price)

          
          template.querySelector('.product__price').textContent = price;
          template.querySelector('img').setAttribute('src', url)
          template.setAttribute('href', location.origin + product.url)
          template.setAttribute('title', product.title)
          template.querySelector('span').textContent = product.title;

          console.log('Cart Product', product)

          const hasOnlyDefaultVariant = product?.variants?.length === 1;
          const defaultVariant = product?.variants[0];

          console.log('Cart Product Template', template);
          console.log('Cart Product hasOnlyDefaultVariant', hasOnlyDefaultVariant, defaultVariant)

          if (hasOnlyDefaultVariant && defaultVariant.available === true) {
            const submitButton = template.querySelector('button');

            // on click, add product to cart with default variant
            template.addEventListener('click', async (event) => {
              console.log('Cart Product Click', event)

              // Set loading state
              template.querySelector('.loading-overlay__spinner').classList.remove('hidden');

              event.preventDefault();

              try {
                const variantId = product.variants[0].id;
                const quantity = 1;
                const body = JSON.stringify({ id: variantId, quantity: quantity });
                const res = await fetch(`${routes.cart_add_url}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: `application/json`,
                  },
                  ...{ body }
                });

                const json = res.json();


                setTimeout(() => {
                  const event = new CustomEvent('ajaxProduct:added', {
                    detail: json,
                  });
                  document.dispatchEvent(event);
                }, 150)

              } catch(error) {
                const err = err?.response?.data?.description || err?.message || error;
                alert(error)
              } finally {
                template.querySelector('.loading-overlay__spinner').classList.add('hidden');
              }
            });
          }

          this.productsContainer.appendChild(template)
        })
      } catch(error) {
        this.setAttribute('hidden', true)
        console.warn(error)
      }
    }
  })
}